import "./DistanceMeasurer.css";

import Overlay from "ol/Overlay";
import { vector } from "../../_config/layers/";
import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
} from "ol/style";
import { LineString } from "ol/geom";
import { getLength } from "ol/sphere";
import { unByKey } from "ol/Observable";
import Draw from "ol/interaction/Draw";

import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

import { source } from "../../_config/layers/vector";
import { useTranslation } from "react-i18next";

import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";

let draw = null;
let sketch = null;

let helpTooltipElement = null;
let helpTooltip = null;

let measureTooltipElement = null;
let measureTooltip = null;

const DistanceMeasurer = forwardRef((props, ref) => {
  const { t } = useTranslation();

  const htmlElement = useRef(null);

  const [active, setActive] = useState(false);

  const formatLength = (line) => {
    const length = getLength(line, {
      projection: "EPSG:4326",
    });

    return length > 100
      ? `${Math.round((length / 1000) * 100) / 100} ${t(
          "distanceMeasurer.km"
        )}`
      : `${Math.round(length * 100) / 100} ${t(
          "distanceMeasurer.m"
        )}`;
  };

  const pointerMoveHandler = (evt) => {
    if (evt.dragging || !helpTooltipElement || !helpTooltip) {
      return;
    }

    helpTooltipElement.innerHTML = sketch
      ? t("distanceMeasurer.continueMsg")
      : t("distanceMeasurer.initMsg");

    helpTooltip.setPosition(evt.coordinate);
    helpTooltipElement.classList.remove("hidden");
  };

  const createHelpTooltip = () => {
    if (helpTooltipElement) {
      helpTooltipElement.remove();
    }

    helpTooltipElement = document.createElement("div");
    helpTooltipElement.className = "ol-tooltip hidden";

    helpTooltip = new Overlay({
      element: helpTooltipElement,
      offset: [15, 0],
      positioning: "center-left",
    });

    props.map.addOverlay(helpTooltip);
  };

  const createMeasureTooltip = () => {
    if (measureTooltipElement) {
      measureTooltipElement.remove();
    }

    measureTooltipElement = document.createElement("div");
    measureTooltipElement.className =
      "ol-tooltip ol-tooltip-measure";

    measureTooltip = new Overlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: "bottom-center",
      stopEvent: false,
      insertFirst: false,
    });

    props.map.addOverlay(measureTooltip);
  };

  const addInteraction = () => {
    if (!props.map) {
      return;
    }

    if (draw) {
      props.map.removeInteraction(draw);
      draw = null;
    }

    draw = new Draw({
      source,
      type: "LineString",
      style: new Style({
        fill: new Fill({
          color: "rgba(255,255,255,0.2)",
        }),
        stroke: new Stroke({
          color: "rgba(0,0,0,0.5)",
          lineDash: [10, 10],
          width: 2,
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: "rgba(0,0,0,0.7)",
          }),
          fill: new Fill({
            color: "rgba(255,255,255,0.2)",
          }),
        }),
      }),
    });

    props.map.addInteraction(draw);

    createHelpTooltip();
    createMeasureTooltip();

    let listener = null;

    draw.on("drawstart", (evt) => {
      sketch = evt.feature;

      let tooltipCoord = evt.coordinate;

      listener = sketch.getGeometry().on("change", (evt) => {
        const geom = evt.target;

        if (!measureTooltipElement || !measureTooltip) {
          return;
        }

        if (geom instanceof LineString) {
          measureTooltipElement.innerHTML = formatLength(geom);

          tooltipCoord = geom.getLastCoordinate();

          measureTooltip.setPosition(tooltipCoord);
        }
      });
    });

    draw.on("drawend", () => {
      if (measureTooltipElement) {
        measureTooltipElement.className =
          "ol-tooltip ol-tooltip-static";
      }

      if (measureTooltip) {
        measureTooltip.setOffset([0, -7]);
      }

      sketch = null;

      if (listener) {
        unByKey(listener);
        listener = null;
      }

      measureTooltipElement = null;

      createMeasureTooltip();
    });
  };

  const handleActivity = (activate) => {
    if (!props.map) {
      return;
    }

    if (activate) {
      props.map.on("pointermove", pointerMoveHandler);

      createHelpTooltip();
      createMeasureTooltip();

      addInteraction();
    } else {
      props.map.un("pointermove", pointerMoveHandler);

      if (draw) {
        props.map.removeInteraction(draw);
        draw = null;
      }

      if (helpTooltip) {
        props.map.removeOverlay(helpTooltip);
        helpTooltip = null;
      }

      if (helpTooltipElement) {
        helpTooltipElement.remove();
        helpTooltipElement = null;
      }

      if (measureTooltip) {
        props.map.removeOverlay(measureTooltip);
        measureTooltip = null;
      }

      measureTooltipElement = null;
      sketch = null;

      source.clear();

      document
        .querySelectorAll(".ol-tooltip-static")
        .forEach((el) => el.remove());
    }
  };

  useImperativeHandle(ref, () => ({
    deactivate: () => {
      setActive(false);

      if (props.handleActiveTool) {
        props.handleActiveTool(null);
      }
    },
  }));

  useEffect(() => {
    handleActivity(active);

    return () => {
      handleActivity(false);
    };
  }, [active]);

  useEffect(() => {
    if (
      props.activeTool &&
      props.activeTool !== htmlElement
    ) {
      setActive(false);
    }
  }, [props.activeTool]);

  const handleClick = () => {
    const next = !active;

    vector.getSource().clear();
    source.clear();

    setActive(next);

    if (props.handleActiveTool) {
      props.handleActiveTool(
        next ? htmlElement : null
      );
    }
  };

  return (
    <>
      <Button
        alt={t("distanceMeasurer.buttonTooltip")}
        title={t("distanceMeasurer.buttonTooltip")}
        onClick={handleClick}
        ref={htmlElement}
        className={`DistanceMeasurerButton DistanceMeasurer ${
          active
            ? "DistanceMeasurerActive"
            : "DistanceMeasurerDeactive"
        }`}
        severity="info"
        rounded
        icon="pi pi-arrows-h"
      />

      <Tooltip
        target=".DistanceMeasurerButton"
        content={t("distanceMeasurer.buttonTooltip")}
        position="bottom"
        className="CustomTooltip"
      />
    </>
  );
});

DistanceMeasurer.displayName = "DistanceMeasurer";

export default DistanceMeasurer;